import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { DailyLogOperationInstance, TimeMaskOptions, TypeSwitch } from '../../../models/projects/operation.models';
import { SetupService } from '../../../services/setup/setup.service';
import { DateService } from '../../../services/date/date.service';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { ApiOption } from '../../../_foundation/models/base.models';
import { DelayService } from '../../../services/delay/delay-service/delay.service';
import { cloneDeep } from 'lodash';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-operation-split',
  templateUrl: 'operation-split.html',
})
export class OperationSplitPage {
  config = AppConfig.settings;
  instance: DailyLogOperationInstance;
  unsavedChanges: boolean = false;
  showWarning: boolean = false;
  splits: TypeSwitch[] = [];
  typeSwitchIdsToDelete: number[] = [];
  isWeb: boolean;
  showError = false;
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  editor: TypeSwitch = new TypeSwitch();
  dailyLogDate: DateTime;
  min: string;
  max: string;
  maxDate: DateTime;
  minDate: DateTime;
  popFn = null;
  callback: Function;
  operationTypes: ApiOption<boolean>[] = [];

  instanceFullStartTime: string;
  instanceFullEndTime: string;

  constructor(
    public navCtrl: NavController,
    public platform: Platform,
    public navParams: NavParams,
    public setupService: SetupService,
    public dateService: DateService,
    public operationService: OperationService,
    public dailyLogService: DailyLogService,
    public navHelperService: NavHelperService,
    public delayService: DelayService,
    public lockedDayService: LockedDayService,
    public modalController: ModalController,
    private supportContextService: SupportContextService,
  ) {
    this.instance = navParams.get('instance');
    this.dailyLogDate = navParams.get('dailyLogDate');

    this.instanceFullStartTime = this.dateService.setFullTimeString(this.instance.startTime.dateTime);
    this.instanceFullEndTime = this.dateService.setFullTimeString(this.instance.endTime.dateTime);

    this.typeSwitchIdsToDelete = [];
    const contractual = new ApiOption<boolean>();
    contractual.id = false;
    contractual.name = 'Contractual';

    const concurrent = new ApiOption<boolean>();
    concurrent.id = true;
    concurrent.name = 'Concurrent';

    this.operationTypes.push(contractual);
    this.operationTypes.push(concurrent);

    let start = this.instance.startTime.dateTime > this.dailyLogDate
      ? this.instance.startTime.dateTime
      : this.dailyLogDate;

    if (!start) {
      start = DateTime.local();
    }

    this.minDate = start;
    // this.maxDate set in ngOnInit because we cant have an async constructor

    this.editor.date = this.instance.startTime.dateTime < this.dailyLogDate
      ? new PtLuxonDate(this.dailyLogDate)
      : cloneDeep(this.instance.startTime);
    this.editor.dateOffset = this.editor.date.dateTime.offset / 60;
    this.editor.dateToShow = this.dateService.setTimeStringFromPtLuxonDate(this.editor.date);
    this.editor.fullDateString = this.dateService.setFullTimeString(this.editor.date.dateTime);

    this.min = '00:00';

    const end = this.instance.endTime;

    let endMax = '23:59';

    if (!end && !end.dateTime) {
      endMax = this.dateService.setTimeStringFromPtLuxonDate(end);
    }

    this.max = '23:59';
  }

  async ngOnInit() {
    this.maxDate = await this.dateService.addDuration(this.minDate, {day: 1}, true);
  }

  ionViewCanLeave() {
    if (this.unsavedChanges && !this.showWarning) {
      this.showWarning = true;
      return false;
    }
    return true;
  }

  async ionViewDidLoad() {
    this.isWeb = this.platform.is('core');
    this.splits = this.instance.switchTimes != null ? this.instance.switchTimes : [];

    if (!!this.splits) {
      this.splits.sort((a, b) => (!a.date.dateTime ? 0 : a.date.dateTime.toMillis())
        - (!b.date.dateTime ? 0 : b.date.dateTime.toMillis()));

      for (let i = 0; i < this.splits.length; i++) {
        this.splits[i].fullDateString = await this.dateService.setFullTimeString(this.splits[i].date.dateTime);
      }
    }

    this.checkForDuplicateSplits();
  }

  ionViewDidEnter() {
    this.supportContextService.setComponentContext(
      SupportContextKey.EditOperation,
      'Operation Split',
      'OperationSplitPage',
      this.getOperationSupportDetails()
    );

    this.popFn = this.navCtrl.pop;

    this.callback = this.navParams.get('callback');
  }

  save() {
    this.instance.switchTimes = this.instance.switchTimes.filter(p => p.delayApiId != null);
    this.instance.switchTimes = this.instance.switchTimes.concat(cloneDeep(this.splits));
    this.instance.typeSwitchIdsToDelete = this.typeSwitchIdsToDelete;

    this.callback(this.unsavedChanges);

    this.unsavedChanges = false;

    this.navCtrl.pop();
  }

  async addSwitchTime() {
    const result = await this.validateSwitchTime();

    if (!result) {
      this.showError = true;
      setTimeout(() => {
        this.showError = false;
      }, 2000);
      return;
    }

    this.editor.fullDateString = this.dateService.setFullTimeString(this.editor.date.dateTime);
    this.unsavedChanges = true;
    const switchToAdd = cloneDeep(this.editor);
    switchToAdd.id = this.delayService.calculateSwitchId(this.splits);

    this.splits.push(switchToAdd);

    this.splits.sort((a, b) => (!a.date.dateTime ? 0 : a.date.dateTime.toMillis())
      - (!b.date.dateTime ? 0 : b.date.dateTime.toMillis()));

    this.checkForDuplicateSplits();
  }

  async validateSwitchTime(): Promise<boolean> {
    // this.date is local start of day
    const time = await this.dateService.setPtLuxonDateFromUserEnteredString(this.editor.dateToShow, this.editor.date.dateTime);

    if (time.dateTime.toMillis() < this.instance.startTime.dateTime.toMillis()) {
      return false;
    }

    return !(!!this.instance.endTime.dateTime && time.dateTime.toMillis() > this.instance.endTime.dateTime.toMillis());
  }

  removeTime(id: number) {
    this.unsavedChanges = true;

    this.typeSwitchIdsToDelete.push(id);
    this.splits = this.splits.filter(p => p.id !== id);

    this.checkForDuplicateSplits();
  }

  async timeChanged(value: string) {
    const timeValid = this.dateService.timeIsValid(value);

    if (!timeValid || value === '') {
      return;
    }

    const timeAllowed = await this.dateService.timeAllowed(
      value, this.editor.date && this.editor.date.dateTime ? this.editor.date.dateTime : this.dailyLogDate
    );

    if (!timeAllowed) {
      return;
    }

    this.editor.date = await this.dateService.setPtLuxonDateFromUserEnteredString(value, this.editor.date && this.editor.date.dateTime
      ? this.editor.date.dateTime
      : this.dailyLogDate);
    this.editor.dateOffset = this.editor.date.dateTime.offset / 60;
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  offsetSelected(ptLuxonDate: PtLuxonDate) {
    this.editor.dateOffset = ptLuxonDate.dateTime.offset / 60;
  }

  // Gets called whenever the user changes a start or end time to configure the correct regex pattern.
  regexChange(timeEntered: string) {

    if (timeEntered === null || timeEntered === undefined || timeEntered === '') {
      return;
    }

    const digits = this.calculateEnteredDigits(timeEntered);

    this.mask[1] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString(), 10),
      digitCombo: digits[0],
      totalMaxDigit: 9,
      position: 1,
    });

    this.mask[3] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString() + this.min.charAt(1).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString() + this.max.charAt(1).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString(), 10),
      totalMaxDigit: 5,
      position: 3,
    });

    this.mask[4] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString() + this.min.charAt(1).toString() + this.min.charAt(3).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString() + this.max.charAt(1).toString() + this.max.charAt(3).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString() + digits[2].toString(), 10),
      totalMaxDigit: 9,
      position: 4,
    });
  }

  private configureTimeMask(options: TimeMaskOptions): RegExp {

    if (options.minDigit === options.maxDigit) {
      return new RegExp(`[${this.min.charAt(options.position)}` + '-' + `${this.max.charAt(options.position)}]`);
    }

    if (options.minDigit === options.digitCombo) {
      return new RegExp(`[${this.min.charAt(options.position)}` + '-' + `${options.totalMaxDigit}]`);
    }

    if (options.maxDigit === options.digitCombo) {
      return new RegExp(`[0` + '-' + `${this.max.charAt(options.position)}]`);
    }

    return new RegExp(`[0-${options.totalMaxDigit}]`);
  }

  private calculateEnteredDigits(timeEnter: string): number[] {
    const numbers = [];
    numbers.push(Number.parseInt(timeEnter.charAt(0), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(1), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(3), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(4), 10));
    return numbers;
  }

  // if a user logs a split during a time that is already of that type, then we will show a warning that it's not necessary
  // i.e. operation is running as concurrent already from 00:00 to 12:00, if they log a concurrent split at 08:00 - it's already concurrent
  // it's not needed - and logging duplicate splits like this can mess up the daily log summary timings
  private checkForDuplicateSplits() {
    this.splits.forEach(e => {
      const previousSplitIndex = this.splits.findIndex(p => p.id === e.id) - 1;

      const previousIsConcurrent = previousSplitIndex >= 0
        ? this.splits[previousSplitIndex].isConcurrent
        : this.instance.isConcurrentOperation;

      // if the last split is the same type as the one being added, then we don't need to add it
      e.splitNotNeeded = previousIsConcurrent === e.isConcurrent;
    });
  }

  private getOperationSupportDetails(): {[key: string]: string} {
    return {
      instanceId: this.getOperationInstanceId(),
      operationName: this.getOperationName()
    };
  }

  private getOperationInstanceId(): string {
    if (!this.instance) {
      return '';
    }

    return this.instance.id != null
      ? `${this.instance.id}`
      : this.instance.localId != null
        ? `${this.instance.localId}`
        : '';
  }

  private getOperationName(): string {
    if (!this.instance || !this.instance.projectSpecificOperation) {
      return '';
    }

    return this.instance.projectSpecificOperation.name
      || this.instance.projectSpecificOperation.operationSummary?.name
      || '';
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-split/operation-split.ts